import { type DragEvent, type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';

import {
  addContextText,
  addContextUrl,
  CONTEXT_FILE_EXTENSIONS,
  type ContextItem,
  type ContextItemLabels,
  type ContextItemType,
  contextItemFileUrl,
  contextItemScreenshotUrl,
  contextTextBody,
  deleteContextItem,
  fetchContextItems,
  MAX_CONTEXT_BYTES_PER_BOOK,
  MAX_CONTEXT_ITEM_BYTES,
  MAX_CONTEXT_ITEM_NOTE_LENGTH,
  MAX_CONTEXT_ITEM_TITLE_LENGTH,
  MAX_CONTEXT_ITEMS_PER_BOOK,
  MAX_CONTEXT_TEXT_CHARS,
  MAX_JSON_BODY_BYTES,
  retryContextItem,
  updateContextItem,
  uploadContextFile,
} from './api.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { describeError, redirectIfUnauthorised } from './data.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { useToast } from './toast.tsx';
import { Badge, bytesLabel, EmptyState, Notice, Panel, Segmented, Skeleton, VisuallyHidden } from './ui.tsx';

const TYPE_ICON: Record<ContextItemType, IconName> = {
  url: 'link-external',
  pdf: 'file',
  docx: 'file',
  image: 'image',
  text: 'quote',
};

const TYPE_LABEL: Record<ContextItemType, string> = {
  url: 'Web page',
  pdf: 'PDF',
  docx: 'Word document',
  image: 'Image',
  text: 'Text',
};

/** The limits in the server's words, shown in the dialog before anything is sent. */
const LIMITS_TEXT =
  `One item is at most ${bytesLabel(MAX_CONTEXT_ITEM_BYTES)} ` +
  `(pasted text at most ${MAX_CONTEXT_TEXT_CHARS.toLocaleString()} characters); ` +
  `a brand book holds at most ${String(MAX_CONTEXT_ITEMS_PER_BOOK)} items and ` +
  `${bytesLabel(MAX_CONTEXT_BYTES_PER_BOOK)} together.`;

/**
 * A brand book's context items (US-013): the marketing plan, the competitor's
 * page, the pasted positioning notes — read by every content made in the
 * book beside its brand profile.
 *
 * The list is this panel's own state: an add, an edit or a delete answers
 * with the item (or nothing), and the list is patched with that, never
 * re-read, so nothing reloads.
 */
export function ContextItemsPanel({
  brandBookId,
  name,
}: {
  readonly brandBookId: string;
  readonly name: string;
}) {
  const toast = useToast();
  const [items, setItems] = useState<ContextItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<ContextItem | null>(null);
  const [retrying, setRetrying] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    const controller = new AbortController();
    setItems(null);
    fetchContextItems(brandBookId, controller.signal)
      .then((found) => {
        setItems(found);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      });
    return () => controller.abort();
  }, [brandBookId]);

  const replace = (item: ContextItem): void => {
    setItems((current) => current?.map((candidate) => (candidate.id === item.id ? item : candidate)) ?? null);
  };

  const retry = (item: ContextItem): void => {
    setRetrying((current) => new Set(current).add(item.id));
    retryContextItem(item)
      .then((answer) => {
        replace(answer);
        if (answer.status === 'ready') toast.ok(`Read ${answer.title} again.`);
        else toast.error(`Could not read ${answer.title}: ${answer.error ?? 'unknown error'}`);
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setRetrying((current) => {
          const next = new Set(current);
          next.delete(item.id);
          return next;
        });
      });
  };

  const count = items?.length ?? 0;
  const totalBytes = items?.reduce((sum, item) => sum + item.bytes, 0) ?? 0;
  const full = count >= MAX_CONTEXT_ITEMS_PER_BOOK;

  return (
    <Panel
      title="Context"
      icon="stack"
      meta={
        items === null ? null : (
          <Badge tone={count === 0 ? 'neutral' : 'done'}>
            {`${String(count)}/${String(MAX_CONTEXT_ITEMS_PER_BOOK)} items · ${bytesLabel(totalBytes)}`}
          </Badge>
        )
      }
      actions={
        <button
          type="button"
          className="button button--small"
          onClick={() => setAdding(true)}
          disabled={items === null || full}
          title={full ? `A brand book holds at most ${String(MAX_CONTEXT_ITEMS_PER_BOOK)} items. Delete one first.` : undefined}
        >
          <Icon name="plus" />
          Add context
        </button>
      }
    >
      {error !== null && <Notice kind="error">Could not load the context items: {error}</Notice>}
      {items === null ? (
        error === null && <Skeleton lines={3} />
      ) : items.length === 0 ? (
        <EmptyState icon="stack" title="No context yet">
          <p>
            Attach a marketing plan, a web page or pasted notes. Every content made in {name} reads
            them beside its brand profile.
          </p>
        </EmptyState>
      ) : (
        <ul className="context-items">
          {items.map((item) => (
            <ContextItemRow
              key={item.id}
              item={item}
              retrying={retrying.has(item.id)}
              onChanged={replace}
              onRetry={() => retry(item)}
              onDelete={() => setDeleting(item)}
            />
          ))}
        </ul>
      )}
      {adding && items !== null && (
        <AddContextDialog
          brandBookId={brandBookId}
          count={count}
          totalBytes={totalBytes}
          onAdded={(item) => {
            setItems((current) => [...(current ?? []), item]);
            setAdding(false);
            if (item.status === 'ready') toast.ok(`Added ${item.title} to ${name}.`);
            else toast.error(`Added ${item.title}, but the page could not be read: ${item.error ?? 'unknown error'}`);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      {deleting !== null && (
        <DeleteContextDialog
          item={deleting}
          onDeleted={(item) => {
            setItems((current) => current?.filter((candidate) => candidate.id !== item.id) ?? null);
            setDeleting(null);
            toast.ok(`Deleted ${item.title}.`);
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </Panel>
  );
}

function ContextItemRow({
  item,
  retrying,
  onChanged,
  onRetry,
  onDelete,
}: {
  readonly item: ContextItem;
  readonly retrying: boolean;
  readonly onChanged: (item: ContextItem) => void;
  readonly onRetry: () => void;
  readonly onDelete: () => void;
}) {
  const toast = useToast();
  const [thumbnailBroken, setThumbnailBroken] = useState(false);
  const hasScreenshot = item.type === 'url' && item.snapshotTakenAt !== null && !thumbnailBroken;

  const save = async (patch: { title?: string; note?: string | null }): Promise<boolean> => {
    try {
      onChanged(await updateContextItem(item, patch));
      return true;
    } catch (cause: unknown) {
      if (!redirectIfUnauthorised(cause)) toast.error(describeError(cause));
      return false;
    }
  };

  return (
    <li className={`context-item${item.status === 'failed' ? ' context-item--failed' : ''}`}>
      <div className="context-item__thumb" aria-hidden={!hasScreenshot}>
        {hasScreenshot ? (
          <a href={contextItemScreenshotUrl(item)} target="_blank" rel="noreferrer">
            <img
              src={`${contextItemScreenshotUrl(item)}?t=${encodeURIComponent(item.snapshotTakenAt ?? '')}`}
              alt={`Screenshot of ${item.url ?? item.title}`}
              loading="lazy"
              onError={() => setThumbnailBroken(true)}
            />
          </a>
        ) : (
          <Icon name={TYPE_ICON[item.type]} />
        )}
      </div>
      <div className="context-item__body">
        <InlineText
          label="Title"
          value={item.title}
          maxLength={MAX_CONTEXT_ITEM_TITLE_LENGTH}
          required
          onSave={(title) => save({ title })}
        />
        <InlineText
          label="Note"
          value={item.note ?? ''}
          maxLength={MAX_CONTEXT_ITEM_NOTE_LENGTH}
          multiline
          placeholder="Add a note"
          onSave={(note) => save({ note: note === '' ? null : note })}
        />
        <p className="context-item__meta">
          <span>{TYPE_LABEL[item.type]}</span>
          {item.url !== null ? (
            <a className="link mono" href={item.url} target="_blank" rel="noreferrer">
              {item.url}
            </a>
          ) : (
            <span className="mono">
              {item.originalName === null ? '' : `${item.originalName} · `}
              {bytesLabel(item.bytes)}
            </span>
          )}
          {item.status === 'ready' ? <Badge tone="done">ready</Badge> : <Badge tone="danger">failed</Badge>}
        </p>
        {item.status === 'failed' && (
          <Notice kind="error">
            {item.error ?? 'The page could not be read.'}{' '}
            <button type="button" className="button button--small" onClick={onRetry} disabled={retrying}>
              <Icon name="sync" />
              {retrying ? 'Retrying…' : 'Retry'}
            </button>
          </Notice>
        )}
      </div>
      <div className="context-item__actions">
        {item.type === 'url' ? (
          item.status === 'ready' && (
            <button type="button" className="button button--small" onClick={onRetry} disabled={retrying}>
              <Icon name="sync" />
              {retrying ? 'Reading…' : 'Read again'}
            </button>
          )
        ) : (
          <a
            className="button button--small"
            href={contextItemFileUrl(item)}
            download={item.originalName ?? (item.type === 'text' ? 'text.md' : '')}
          >
            <Icon name="file" />
            Download
          </a>
        )}
        <button
          type="button"
          className="button button--small button--icon button--danger"
          onClick={onDelete}
          title={`Delete ${item.title}`}
        >
          <Icon name="trash" />
          <VisuallyHidden>Delete {item.title}</VisuallyHidden>
        </button>
      </div>
    </li>
  );
}

/**
 * A title or note that reads as text and turns into a field on click. Enter
 * (or Ctrl+Enter in a note) saves, Escape puts the old value back.
 */
function InlineText({
  label,
  value,
  maxLength,
  required = false,
  multiline = false,
  placeholder,
  onSave,
}: {
  readonly label: string;
  readonly value: string;
  readonly maxLength: number;
  readonly required?: boolean;
  readonly multiline?: boolean;
  readonly placeholder?: string;
  readonly onSave: (value: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const trimmed = draft.trim();
  const invalid = (required && trimmed === '') || trimmed.length > maxLength;

  const start = (): void => {
    setDraft(value);
    setEditing(true);
  };

  const submit = (event?: FormEvent): void => {
    event?.preventDefault();
    if (invalid || saving) return;
    if (trimmed === value.trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    void onSave(trimmed).then((saved) => {
      setSaving(false);
      if (saved) setEditing(false);
    });
  };

  if (!editing) {
    return (
      <button
        type="button"
        className={`context-item__edit${multiline ? ' context-item__edit--note' : ' context-item__edit--title'}${value === '' ? ' muted' : ''}`}
        onClick={start}
        title={`Edit the ${label.toLowerCase()}`}
      >
        <span>{value === '' ? placeholder : value}</span>
        <Icon name="pencil" />
      </button>
    );
  }

  const common = {
    className: 'field__input',
    value: draft,
    'aria-label': label,
    'aria-invalid': invalid,
    disabled: saving,
    autoFocus: true,
    onChange: (event: { target: { value: string } }) => setDraft(event.target.value),
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setEditing(false);
      } else if (event.key === 'Enter' && (!multiline || event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        submit();
      }
    },
  };

  return (
    <form className="context-item__editing" onSubmit={submit}>
      {multiline ? <textarea {...common} rows={3} /> : <input {...common} autoComplete="off" />}
      <span className="field__actions">
        <button type="submit" className="button button--primary button--small" disabled={invalid || saving}>
          <Icon name={saving ? 'sync' : 'check'} />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="button button--small" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </button>
        <span className="field__hint">
          {`${trimmed.length.toLocaleString()}/${maxLength.toLocaleString()}`}
          {multiline ? ' · Ctrl+Enter saves' : ''}
        </span>
      </span>
    </form>
  );
}

type AddTab = 'file' | 'url' | 'text';

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

/** Why what is in the dialog cannot be posted, said before a byte is sent; `null` when it can. */
function refusalFor(
  tab: AddTab,
  input: { file: File | null; url: string; text: string; labels: ContextItemLabels },
  book: { count: number; totalBytes: number },
): string | null {
  if (book.count >= MAX_CONTEXT_ITEMS_PER_BOOK) {
    return `This brand book already holds ${String(MAX_CONTEXT_ITEMS_PER_BOOK)} items. Delete one first.`;
  }
  if (input.labels.title.trim().length > MAX_CONTEXT_ITEM_TITLE_LENGTH) {
    return `A title is at most ${String(MAX_CONTEXT_ITEM_TITLE_LENGTH)} characters.`;
  }
  if (input.labels.note.trim().length > MAX_CONTEXT_ITEM_NOTE_LENGTH) {
    return `A note is at most ${MAX_CONTEXT_ITEM_NOTE_LENGTH.toLocaleString()} characters.`;
  }
  if (tab === 'file') {
    const { file } = input;
    if (file === null) return null;
    if (!(CONTEXT_FILE_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
      return `${file.name} is not a type a context item can be. Use ${CONTEXT_FILE_EXTENSIONS.join(' ')}.`;
    }
    if (file.size === 0) return `${file.name} is empty.`;
    if (file.size > MAX_CONTEXT_ITEM_BYTES) {
      return `${file.name} is ${bytesLabel(file.size)}; one item is at most ${bytesLabel(MAX_CONTEXT_ITEM_BYTES)}.`;
    }
    if (book.totalBytes + file.size > MAX_CONTEXT_BYTES_PER_BOOK) {
      return `${file.name} would take this brand book past ${bytesLabel(MAX_CONTEXT_BYTES_PER_BOOK)} (${bytesLabel(book.totalBytes)} used). Delete an item first.`;
    }
    return null;
  }
  if (tab === 'url') {
    const url = input.url.trim();
    if (url === '') return null;
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return 'Type the whole address, starting with https://.';
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'Use an http:// or https:// address.';
    }
    return null;
  }
  if (input.text.length > MAX_CONTEXT_TEXT_CHARS) {
    return `That is ${input.text.length.toLocaleString()} characters; pasted text is at most ${MAX_CONTEXT_TEXT_CHARS.toLocaleString()}.`;
  }
  if (new Blob([contextTextBody(input.text, input.labels)]).size > MAX_JSON_BODY_BYTES) {
    return `That text is over ${bytesLabel(MAX_JSON_BODY_BYTES)} once sent. Save it as a .md file and upload that instead.`;
  }
  return null;
}

function AddContextDialog({
  brandBookId,
  count,
  totalBytes,
  onAdded,
  onCancel,
}: {
  readonly brandBookId: string;
  readonly count: number;
  readonly totalBytes: number;
  readonly onAdded: (item: ContextItem) => void;
  readonly onCancel: () => void;
}) {
  const [tab, setTab] = useState<AddTab>('file');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const labels = { title, note };
  const refusal = refusalFor(tab, { file, url, text, labels }, { count, totalBytes });
  const empty =
    tab === 'file' ? file === null : tab === 'url' ? url.trim() === '' : text.trim() === '';

  const submit = (): void => {
    if (busy || empty || refusal !== null) return;
    setBusy(true);
    setError(null);
    setProgress(null);
    const request =
      tab === 'file' && file !== null
        ? uploadContextFile(brandBookId, file, labels, (sent, total) => setProgress({ sent, total }))
        : tab === 'url'
          ? addContextUrl(brandBookId, url, labels)
          : addContextText(brandBookId, text, labels);
    request
      .then(onAdded)
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      })
      .finally(() => {
        setBusy(false);
        setProgress(null);
      });
  };

  const onDrop = (event: DragEvent): void => {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped !== undefined && !busy) setFile(dropped);
  };

  const percent = progress === null || progress.total === 0 ? 0 : Math.round((progress.sent / progress.total) * 100);

  return (
    <ConfirmDialog
      open
      title="Add context"
      confirmLabel="Add"
      busyLabel={tab === 'file' ? 'Uploading…' : tab === 'url' ? 'Reading the page…' : 'Adding…'}
      busy={busy}
      confirmDisabled={empty || refusal !== null}
      onConfirm={submit}
      onCancel={onCancel}
    >
      <form
        className="form context-add"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Segmented
          value={tab}
          options={[
            { value: 'file', label: 'File' },
            { value: 'url', label: 'Web page' },
            { value: 'text', label: 'Text' },
          ]}
          onChange={(next) => {
            if (busy) return;
            setTab(next);
            setError(null);
          }}
          ariaLabel="Kind of context"
        />

        {tab === 'file' && (
          <div className="field">
            <span className="field__label">File</span>
            <div
              className={`context-add__drop${dragging ? ' context-add__drop--over' : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <Icon name="file" />
              {file === null ? (
                <span>Drop a file here, or</span>
              ) : (
                <span className="mono">
                  {file.name} · {bytesLabel(file.size)}
                </span>
              )}
              <button
                type="button"
                className="button button--small"
                onClick={() => picker.current?.click()}
                disabled={busy}
              >
                {file === null ? 'Choose a file' : 'Choose another'}
              </button>
              <input
                ref={picker}
                type="file"
                hidden
                accept={CONTEXT_FILE_EXTENSIONS.join(',')}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  event.target.value = '';
                }}
              />
            </div>
            <p className="field__hint">PDF, Word (.docx), PNG, JPEG, WebP, SVG, plain text or Markdown.</p>
          </div>
        )}

        {tab === 'url' && (
          <div className="field">
            <label className="field__label" htmlFor="context-add-url">
              Address of the page
            </label>
            <input
              id="context-add-url"
              className="field__input mono"
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://example.com/pricing"
              autoComplete="off"
              disabled={busy}
            />
            <p className="field__hint">
              The page is read now — its text and a screenshot are kept — and again whenever the brand book is
              refreshed.
            </p>
          </div>
        )}

        {tab === 'text' && (
          <div className="field">
            <label className="field__label" htmlFor="context-add-text">
              Text
            </label>
            <textarea
              id="context-add-text"
              className="field__input field__textarea"
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={8}
              placeholder="Paste the marketing plan, notes from a call, a competitor's positioning…"
              disabled={busy}
            />
            <p className="field__hint">
              Markdown. {text.length.toLocaleString()}/{MAX_CONTEXT_TEXT_CHARS.toLocaleString()} characters.
            </p>
          </div>
        )}

        <div className="field">
          <label className="field__label" htmlFor="context-add-title">
            Title <span className="muted">(optional)</span>
          </label>
          <input
            id="context-add-title"
            className="field__input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={
              tab === 'file' ? 'The file name' : tab === 'url' ? "The page's title" : 'The first line of the text'
            }
            autoComplete="off"
            disabled={busy}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="context-add-note">
            Note <span className="muted">(optional)</span>
          </label>
          <textarea
            id="context-add-note"
            className="field__input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            placeholder="What it is and how to use it, e.g. “Q3 plan — the launch section is what matters.”"
            disabled={busy}
          />
        </div>

        <p className="field__hint">
          {LIMITS_TEXT} This one has {String(count)} {count === 1 ? 'item' : 'items'}, {bytesLabel(totalBytes)}.
        </p>

        {progress !== null && (
          <div className="progress">
            <div
              className="progress__track progress__track--active"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              aria-label="Upload progress"
            >
              <div className="progress__fill" style={{ width: `${String(percent)}%` }} />
            </div>
            <span className="progress__label mono">
              {progress.sent >= progress.total
                ? 'Storing…'
                : `${bytesLabel(progress.sent)} / ${bytesLabel(progress.total)}`}
            </span>
          </div>
        )}
        {busy && tab === 'url' && <Notice kind="info">Reading the page. This can take a minute or two.</Notice>}
        {refusal !== null && <Notice kind="error">{refusal}</Notice>}
        {error !== null && <Notice kind="error">{error}</Notice>}
      </form>
    </ConfirmDialog>
  );
}

function DeleteContextDialog({
  item,
  onDeleted,
  onCancel,
}: {
  readonly item: ContextItem;
  readonly onDeleted: (item: ContextItem) => void;
  readonly onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    setBusy(true);
    setError(null);
    deleteContextItem(item)
      .then(() => onDeleted(item))
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
        setBusy(false);
      });
  };

  return (
    <ConfirmDialog
      open
      title={`Delete ${item.title}?`}
      confirmLabel="Delete"
      busyLabel="Deleting…"
      busy={busy}
      danger
      onConfirm={submit}
      onCancel={onCancel}
    >
      <p>
        {item.type === 'url'
          ? 'The snapshot of this page is deleted with it.'
          : 'The stored original is deleted with it.'}{' '}
        Contents made in this brand book no longer read it from their next step on.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
    </ConfirmDialog>
  );
}
